"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "@/i18n";
import { FadeIn } from "@/components/ui/AnimatedSection";

interface FormData {
    email: string;
    category: string;
    subject: string;
    description: string;
    gameVersion: string;
    platform: string;
}

const initialFormData: FormData = {
    email: "",
    category: "",
    subject: "",
    description: "",
    gameVersion: "",
    platform: "",
};

export function SupportForm() {
    const [form, setForm] = useState<FormData>(initialFormData);
    const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState("");
    const { t } = useTranslation();

    const updateField = (field: keyof FormData, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (status === "error") setStatus("idle");
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus("loading");
        setErrorMessage("");

        try {
            const res = await fetch("/api/support", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => null);
                const safeMessage =
                    res.status < 500 && typeof data?.error === "string"
                        ? data.error
                        : t.support.genericError;

                throw new Error(safeMessage);
            }

            setStatus("success");
            setForm(initialFormData);
        } catch (err) {
            setErrorMessage(
                err instanceof Error && err.message
                    ? err.message
                    : t.support.genericError
            );
            setStatus("error");
        }
    };

    const inputClasses =
        "w-full px-4 py-3 rounded-lg bg-surface border border-border text-text text-sm placeholder:text-text-muted focus:border-mars focus:outline-none transition-colors";
    const labelClasses = "block text-sm font-medium text-text-secondary mb-1.5";

    return (
        <div>
            <FadeIn>
                <h2 className="text-2xl font-bold text-text mb-2">{t.support.title}</h2>
                <p className="text-sm text-text-secondary mb-8">
                    {t.support.subtitle}
                </p>
            </FadeIn>

            <FadeIn delay={0.1}>
                <AnimatePresence mode="wait">
                    {status === "success" ? (
                        <motion.div
                            key="success"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="text-center py-12"
                        >
                            <div className="w-14 h-14 rounded-full bg-terra/10 border border-terra/30 flex items-center justify-center mx-auto mb-4">
                                <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7 text-terra">
                                    <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-text mb-2">
                                {t.support.successTitle}
                            </h3>
                            <p className="text-sm text-text-secondary max-w-sm mx-auto">
                                {t.support.successDetail}
                            </p>
                            <button
                                onClick={() => setStatus("idle")}
                                className="mt-6 text-sm text-mars hover:text-mars-light transition-colors cursor-pointer"
                            >
                                {t.support.successAnother}
                            </button>
                        </motion.div>
                    ) : (
                        <motion.form
                            key="form"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            onSubmit={handleSubmit}
                            className="space-y-5"
                        >
                            {/* Honeypot */}
                            <input
                                type="text"
                                name="website"
                                tabIndex={-1}
                                autoComplete="off"
                                className="absolute opacity-0 pointer-events-none h-0 w-0"
                                aria-hidden="true"
                            />

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                {/* Email */}
                                <div>
                                    <label htmlFor="support-email" className={labelClasses}>
                                        {t.support.email} <span className="text-mars">*</span>
                                    </label>
                                    <input
                                        id="support-email"
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => updateField("email", e.target.value)}
                                        className={inputClasses}
                                        placeholder="your@email.com"
                                        required
                                    />
                                </div>

                                {/* Category */}
                                <div>
                                    <label htmlFor="support-category" className={labelClasses}>
                                        {t.support.category} <span className="text-mars">*</span>
                                    </label>
                                    <select
                                        id="support-category"
                                        value={form.category}
                                        onChange={(e) => updateField("category", e.target.value)}
                                        className={inputClasses}
                                        required
                                    >
                                        <option value="">{t.support.categoryPlaceholder}</option>
                                        {t.support.categories.map((cat) => (
                                            <option key={cat} value={cat}>
                                                {cat}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Subject */}
                            <div>
                                <label htmlFor="support-subject" className={labelClasses}>
                                    {t.support.subject} <span className="text-mars">*</span>
                                </label>
                                <input
                                    id="support-subject"
                                    type="text"
                                    value={form.subject}
                                    onChange={(e) => updateField("subject", e.target.value)}
                                    className={inputClasses}
                                    placeholder={t.support.subjectPlaceholder}
                                    required
                                    minLength={3}
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label htmlFor="support-description" className={labelClasses}>
                                    {t.support.description} <span className="text-mars">*</span>
                                </label>
                                <textarea
                                    id="support-description"
                                    value={form.description}
                                    onChange={(e) => updateField("description", e.target.value)}
                                    className={`${inputClasses} min-h-[120px] resize-y`}
                                    placeholder={t.support.descriptionPlaceholder}
                                    required
                                    minLength={10}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                {/* Game Version */}
                                <div>
                                    <label htmlFor="support-version" className={labelClasses}>
                                        {t.support.gameVersion} <span className="text-text-muted">{t.support.gameVersionOptional}</span>
                                    </label>
                                    <input
                                        id="support-version"
                                        type="text"
                                        value={form.gameVersion}
                                        onChange={(e) => updateField("gameVersion", e.target.value)}
                                        className={inputClasses}
                                        placeholder={t.support.gameVersionPlaceholder}
                                    />
                                </div>

                                {/* Platform */}
                                <div>
                                    <label htmlFor="support-platform" className={labelClasses}>
                                        {t.support.platform} <span className="text-text-muted">{t.support.platformOptional}</span>
                                    </label>
                                    <input
                                        id="support-platform"
                                        type="text"
                                        value={form.platform}
                                        onChange={(e) => updateField("platform", e.target.value)}
                                        className={inputClasses}
                                        placeholder={t.support.platformPlaceholder}
                                    />
                                </div>
                            </div>

                            {/* Error */}
                            <AnimatePresence>
                                {status === "error" && errorMessage && (
                                    <motion.p
                                        initial={{ opacity: 0, y: 5 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        className="text-sm text-red-400"
                                        role="alert"
                                    >
                                        {errorMessage}
                                    </motion.p>
                                )}
                            </AnimatePresence>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={status === "loading"}
                                className="px-8 py-3 rounded-lg bg-mars text-white text-sm font-medium hover:bg-mars-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                            >
                            {status === "loading" ? t.support.submitting : t.support.submit}
                            </button>
                        </motion.form>
                    )}
                </AnimatePresence>
            </FadeIn>
        </div>
    );
}
